import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/lib/queryClient';
import {
  useSearchableInvoices,
  useSubmitClaim,
  type SearchableInvoiceRow,
  type UnclaimedInvoiceRow,
} from '@/hooks/useUnclaimed';
import NewInvoiceStep, {
  type NewInvoiceStepHandle,
  type NewInvoiceStepDraft,
} from './NewInvoiceStep';
import { DEFAULT_TRIP_SECTION } from '@/components/invoicing/TripSection';
import { cn } from '@/lib/utils';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';

interface ClaimWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The unclaimed rows being claimed in this submission. */
  rows: UnclaimedInvoiceRow[];
  /** Fires once per successfully submitted row so the parent can drop it
   *  from its persistent selection IMMEDIATELY (not only on full close). */
  onRowComplete?: (row: UnclaimedInvoiceRow) => void;
  /** Fires when the wizard finishes (last pending row done OR user closes
   *  after at least one success). Parent decides whether to navigate. */
  onComplete: () => void;
}

type WizardMode = 'existing' | 'new';

/**
 * Deterministic idempotency keys derived from `(userId, booking-line)`.
 * MUST stay stable across browser sessions / retries — TRAMS rows cannot
 * be deleted, so a fresh random UUID on a cross-session retry would miss
 * AIVIA's idempotency lookup AND FastAPI's cache, producing duplicate
 * AIVIA invoices and (worse) duplicate TRAMS rows.
 *
 * The `userId` prefix is required because the unclaimed pool is multi-
 * claim by design (multiple advisors can simultaneously claim the same
 * booking-line; accounting arbitrates). Without it, two different
 * advisors deriving the SAME key from the SAME triple would collide on
 * the server's `(claimantUserId, idempotencyKey)` unique — actually the
 * server-side scoping handles that defensively, but keeping the userId
 * in the key gives a second, application-layer guarantee that two
 * advisors' requests can never accidentally short-circuit into each
 * other's claims.
 *
 * AIVIA→FastAPI keys (derived server-side in invoice.service.ts) also
 * mix in `userId`, so per-user retries are isolated against FastAPI's
 * globally-keyed idempotency cache too.
 *
 * Trade-off: if a user retries with edited form values (e.g. corrected
 * supplier), they get the ORIGINAL invoice back and must edit it via
 * `/invoicing`. Acceptable given the no-delete TRAMS constraint.
 *
 * See agent2/handoffs/2026-05-08-unclaimed-trams-idempotency-p1.md.
 */
function deriveClaimIdempotencyKey(
  userId: number,
  row: {
    tramsInvoiceNo: number;
    tramsBookingNo: number;
    tramsAgentBkgNo: number;
  },
): string {
  return `unclaimed-claim-${userId}-${row.tramsInvoiceNo}-${row.tramsBookingNo}-${row.tramsAgentBkgNo}`;
}

function deriveInvoiceIdempotencyKey(
  userId: number,
  row: {
    tramsInvoiceNo: number;
    tramsBookingNo: number;
    tramsAgentBkgNo: number;
  },
): string {
  return `unclaimed-inv-${userId}-${row.tramsInvoiceNo}-${row.tramsBookingNo}-${row.tramsAgentBkgNo}`;
}

/**
 * Pulls the server-side error code (e.g. `UNCLAIMED_EMAIL_SEND_FAILED`) from
 * an `ApiError` payload so we can branch toast copy on it. NestJS exception
 * filters wrap our `{ code, message, retriable }` body inside `data.message`
 * for some classes (HttpException) and at the root for others — defensively
 * read both shapes.
 */
function extractErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError) || !err.data) return null;
  const data = err.data as Record<string, unknown>;
  const direct = typeof data.code === 'string' ? data.code : null;
  if (direct) return direct;
  const nested =
    data.message && typeof data.message === 'object'
      ? (data.message as Record<string, unknown>).code
      : null;
  return typeof nested === 'string' ? nested : null;
}

/**
 * Composite booking-line key — unique per claimable row. Each unclaimed
 * monthly TRAMS invoice contains many booking lines, so we keep state
 * keyed by the full triple.
 */
const rowKey = (row: {
  tramsInvoiceNo: number;
  tramsBookingNo: number;
  tramsAgentBkgNo: number;
}): string =>
  `${row.tramsInvoiceNo}-${row.tramsBookingNo}-${row.tramsAgentBkgNo}`;

/** Next pending row after submit — scan forward then wrap. */
function findNextPendingRowIndex(
  rows: UnclaimedInvoiceRow[],
  submitted: Set<string>,
  fromIdx: number,
): number {
  const isPending = (r: UnclaimedInvoiceRow) =>
    !submitted.has(rowKey(r)) && r.claimStatus === 'available';

  for (let i = fromIdx + 1; i < rows.length; i++) {
    if (isPending(rows[i])) return i;
  }
  for (let i = 0; i < fromIdx; i++) {
    if (isPending(rows[i])) return i;
  }
  return -1;
}

function formatCommissionSummary(row: UnclaimedInvoiceRow): string {
  const traveler = row.primaryTraveler ?? '—';
  const supplier = row.supplierName ?? '—';
  return `TRAMS ${row.tramsInvoiceNo}/${row.tramsBookingNo} · ${traveler} · ${supplier}`;
}

/**
 * Spec — Unclaimed Commission.docx (Claim Action):
 *   "User chooses either to attach the unclaimed item to an existing AIVIA
 *    invoice or to create a new one. The new-invoice path uses the
 *    standard Create Invoice flow (Trip, Description, etc.), pre-populated
 *    from the unclaimed snapshot."
 *
 * Multi-row submissions iterate sequentially (one claim POST per row).
 * The wizard:
 *   1. Lets the user pick existing-vs-new for each row in the queue.
 *   2. For `new`, embeds the standard Create Invoice form prefilled from
 *      the snapshot. On submit, posts atomic trip+invoice, then posts
 *      the claim with `prepopulatedInvoiceId` so the server reuses our
 *      invoice and stamps it on the support email (spec rule #3).
 *   3. For multi-row, uses a per-row stepper that preserves drafts on
 *      Back/Forward. Trip selection is per commission (not shared).
 */
export function ClaimWizardDialog({
  open,
  onOpenChange,
  rows,
  onRowComplete,
  onComplete,
}: ClaimWizardDialogProps) {
  const { toast } = useToast();
  const submitClaim = useSubmitClaim();
  const { user } = useAuth();
  // The wizard is only mounted on a route guarded by `SessionAuthGuard`,
  // so `user` is non-null in practice. The fallback to `0` is a safety
  // net that yields a stable (but obviously wrong) key, surfacing as a
  // server-side rejection rather than a silent collision.
  const userId = user?.id ?? 0;

  const [mode, setMode] = useState<WizardMode>('existing');
  const [search, setSearch] = useState('');
  const [selectedExistingId, setSelectedExistingId] = useState<number | null>(
    null,
  );
  /** Per-row existing-invoice targets — preserved on Back/Forward. */
  const [existingTargets, setExistingTargets] = useState<
    Map<string, number>
  >(new Map());
  /** Carried forward as default for the next row (same invoice reuse). */
  const [lastExistingInvoiceId, setLastExistingInvoiceId] = useState<
    number | null
  >(null);
  /** Attach-existing vs create-new — remembered per commission. */
  const [rowModes, setRowModes] = useState<Map<string, WizardMode>>(
    new Map(),
  );
  const [activeRowIdx, setActiveRowIdx] = useState(0);
  // Per-row idempotency keys — one for the claim POST, one for the atomic
  // invoice POST. Persisted for the lifetime of this dialog open so retries
  // replay against the server.
  const [claimIdempotencyKeys, setClaimIdempotencyKeys] = useState<
    Record<string, string>
  >({});
  const [invoiceIdempotencyKeys, setInvoiceIdempotencyKeys] = useState<
    Record<string, string>
  >({});
  // Per-row drafts (form values + trip selection). Wizard owns these so
  // Back/Forward navigation preserves user edits across remounts.
  const [drafts, setDrafts] = useState<Map<string, NewInvoiceStepDraft>>(
    new Map(),
  );
  // Track rows we've already submitted in this dialog open (idempotency
  // makes retry safe, but the UI still skips them).
  const [submittedRowKeys, setSubmittedRowKeys] = useState<Set<string>>(
    new Set(),
  );
  const [submitting, setSubmitting] = useState(false);

  const stepRef = useRef<NewInvoiceStepHandle | null>(null);

  const hasUnsavedChanges = useCallback((): boolean => {
    if (!open) return false;
    if (mode === 'existing') {
      return selectedExistingId !== null || search.trim().length > 0;
    }
    return stepRef.current?.isDirty() ?? false;
  }, [open, mode, selectedExistingId, search]);

  const { requestLeave } = useUnsavedChangesGuard(hasUnsavedChanges);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    requestLeave(() => onOpenChange(false));
  };

  useEffect(() => {
    if (!open) return;
    const claimKeys: Record<string, string> = {};
    const invoiceKeys: Record<string, string> = {};
    for (const r of rows) {
      const k = rowKey(r);
      claimKeys[k] = deriveClaimIdempotencyKey(userId, r);
      invoiceKeys[k] = deriveInvoiceIdempotencyKey(userId, r);
    }
    setClaimIdempotencyKeys(claimKeys);
    setInvoiceIdempotencyKeys(invoiceKeys);
    // `userId` is bound into both idempotency-key derivations above; if it
    // changes (logout/login or user switch while the dialog is mounted) the
    // keys must be re-derived so we don't accidentally replay the previous
    // user's claim.
  }, [open, rows, userId]);

  useEffect(() => {
    if (!open) return;
    setMode('existing');
    setRowModes(new Map());
    setSearch('');
    setSelectedExistingId(null);
    setExistingTargets(new Map());
    setLastExistingInvoiceId(null);
    // Start at the first row that's still claimable — defensive against
    // stale selection where some rows are now `pending_review` (e.g. a
    // claim was submitted in a previous wizard run that left the row in
    // selection because the user closed the dialog before refresh).
    const firstAvailable = rows.findIndex(
      (r) => r.claimStatus === 'available',
    );
    setActiveRowIdx(firstAvailable === -1 ? 0 : firstAvailable);
    setDrafts(new Map());
    setSubmittedRowKeys(new Set());
  }, [open, userId]);

  useEffect(() => {
    if (!open || rows.length === 0) return;
    setActiveRowIdx((prev) => Math.max(0, Math.min(prev, rows.length - 1)));
  }, [open, rows]);

  const searchResults = useSearchableInvoices(search, mode === 'existing' && open);

  const isMultiRow = rows.length > 1;
  const activeRow = rows[activeRowIdx];
  const activeRowKey = activeRow ? rowKey(activeRow) : '';
  /**
   * Pending rows = `available` (not yet submitted in this dialog open) and
   * — defensively — not already claimed per the freshest list snapshot
   * the row carried in. We use this for the "k of N" header and for the
   * `Submit & continue` vs `Submit & finish` choice so the user is never
   * asked to submit a row whose only outcome would be a server dedup
   * conflict. Position-stable: indexes remain row-aligned so Back works.
   */
  const isRowDone = (r: UnclaimedInvoiceRow): boolean =>
    submittedRowKeys.has(rowKey(r)) || r.claimStatus !== 'available';
  const pendingRows = rows.filter((r) => !isRowDone(r));
  const activeRowPending = activeRow ? !isRowDone(activeRow) : false;
  /** Pending commissions in the batch besides the one on screen. */
  const otherPendingCount =
    pendingRows.length - (activeRowPending ? 1 : 0);
  const showSubmitContinue =
    isMultiRow && activeRowPending && otherPendingCount > 0;
  const showSubmitFinish =
    !isMultiRow || (activeRowPending && otherPendingCount === 0);

  /** Pull current draft from the embedded step and stash it for restore. */
  const captureActiveDraft = (): NewInvoiceStepDraft | null => {
    if (mode !== 'new' || !stepRef.current) return null;
    const draft = stepRef.current.getDraft();
    setDrafts((prev) => {
      const next = new Map(prev);
      next.set(activeRowKey, draft);
      return next;
    });
    return draft;
  };

  /** Apply attach/create + invoice pick for `targetKey` (sync — no useEffect lag). */
  const applyRowModeUi = (
    targetKey: string,
    modes: Map<string, WizardMode>,
  ) => {
    const savedMode = modes.get(targetKey) ?? 'existing';
    setMode(savedMode);
    setSearch('');
    if (savedMode === 'existing') {
      const saved = existingTargets.get(targetKey);
      setSelectedExistingId(
        saved ?? (isMultiRow ? lastExistingInvoiceId : null),
      );
    } else {
      setSelectedExistingId(null);
    }
  };

  const navigateToRow = (idx: number) => {
    if (idx === activeRowIdx || submitting) return;
    captureActiveDraft();

    const nextModes = activeRowKey
      ? (() => {
          const map = new Map(rowModes);
          map.set(activeRowKey, mode);
          return map;
        })()
      : rowModes;
    setRowModes(nextModes);

    const targetRow = rows[idx];
    const targetKey = targetRow ? rowKey(targetRow) : '';
    applyRowModeUi(targetKey, nextModes);
    setActiveRowIdx(idx);
  };

  const jumpToRow = (idx: number) => navigateToRow(idx);

  const goRow = (delta: number) => {
    const next = activeRowIdx + delta;
    if (next < 0 || next >= rows.length || submitting) return;
    navigateToRow(next);
  };

  /** After a successful claim: advance to next pending row or close batch. */
  const completeActiveRowSubmit = () => {
    const submitted = new Set(submittedRowKeys);
    submitted.add(activeRowKey);
    const nextIdx = findNextPendingRowIndex(rows, submitted, activeRowIdx);
    setSubmittedRowKeys(submitted);
    if (nextIdx === -1) {
      onComplete();
      onOpenChange(false);
    } else {
      const targetRow = rows[nextIdx];
      const targetKey = targetRow ? rowKey(targetRow) : '';
      applyRowModeUi(targetKey, rowModes);
      setActiveRowIdx(nextIdx);
    }
  };

  const handleModeChange = (next: WizardMode) => {
    if (next === mode || !activeRowKey) return;
    captureActiveDraft();
    setRowModes((prev) => {
      const map = new Map(prev);
      map.set(activeRowKey, next);
      return map;
    });
    setMode(next);
    setSearch('');
    if (next === 'existing') {
      const saved = existingTargets.get(activeRowKey);
      setSelectedExistingId(
        saved ?? (isMultiRow ? lastExistingInvoiceId : null),
      );
    } else {
      setSelectedExistingId(null);
    }
  };

  const handleSubmitNewRow = async () => {
    if (!activeRow || !stepRef.current) return;
    setSubmitting(true);
    try {
      // 1. Standard Create Invoice flow — atomic trip + invoice POST.
      const { tripId, trip, invoiceId, invoiceNumber } =
        await stepRef.current.submit();

      // 2. Stash this row's draft only — do not carry trip forward; batch
      //    commissions may belong to different trips.
      const lastDraft = stepRef.current.getDraft();
      setDrafts((prev) => {
        const next = new Map(prev);
        next.set(activeRowKey, lastDraft);
        return next;
      });

      // 3. Claim post — server validates ownership + trip match before
      //    reusing our invoice.
      const claimKey =
        claimIdempotencyKeys[activeRowKey] ??
        deriveClaimIdempotencyKey(userId, activeRow);
      await submitClaim.mutateAsync({
        tramsInvoiceNo: activeRow.tramsInvoiceNo,
        tramsBookingNo: activeRow.tramsBookingNo,
        tramsAgentBkgNo: activeRow.tramsAgentBkgNo,
        rowSnapshot: activeRow,
        idempotencyKey: claimKey,
        target: {
          kind: 'new',
          tripId,
          prepopulatedInvoiceId: invoiceId,
        },
      });

      onRowComplete?.(activeRow);

      toast({
        title: `Claim submitted`,
        description: invoiceNumber
          ? `Invoice #${invoiceNumber} sent to support for review.`
          : 'Sent to support for review.',
      });

      completeActiveRowSubmit();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const code = extractErrorCode(err);
      // Email send is the only retriable failure that doesn't change DB
      // state — the server explicitly DOES NOT create the claim row when
      // the support email can't be delivered, so the user is safe to retry
      // with the same idempotency key. Surface that distinction so they
      // don't think their work was lost.
      const isEmailFailure = code === 'UNCLAIMED_EMAIL_SEND_FAILED';
      toast({
        title: isEmailFailure
          ? 'Could not notify support — please retry'
          : `Row ${activeRowIdx + 1} of ${rows.length} failed`,
        description: isEmailFailure
          ? 'Your claim was NOT recorded because the notification email could not be delivered. Click "Submit" again to retry.'
          : submittedRowKeys.size > 0
            ? `${msg}. Earlier rows already submitted.`
            : msg,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitExisting = async () => {
    if (!activeRow || selectedExistingId === null) return;
    setSubmitting(true);
    try {
      const claimKey =
        claimIdempotencyKeys[activeRowKey] ??
        deriveClaimIdempotencyKey(userId, activeRow);
      await submitClaim.mutateAsync({
        tramsInvoiceNo: activeRow.tramsInvoiceNo,
        tramsBookingNo: activeRow.tramsBookingNo,
        tramsAgentBkgNo: activeRow.tramsAgentBkgNo,
        rowSnapshot: activeRow,
        idempotencyKey: claimKey,
        target: { kind: 'existing', invoiceId: selectedExistingId },
      });
      setExistingTargets((prev) => {
        const next = new Map(prev);
        next.set(activeRowKey, selectedExistingId);
        return next;
      });
      setLastExistingInvoiceId(selectedExistingId);
      onRowComplete?.(activeRow);
      toast({
        title: 'Claim submitted',
        description: 'Support has been notified. You will hear back shortly.',
      });

      completeActiveRowSubmit();
    } catch (err) {
      const code = extractErrorCode(err);
      const isEmailFailure = code === 'UNCLAIMED_EMAIL_SEND_FAILED';
      toast({
        title: isEmailFailure
          ? 'Could not notify support — please retry'
          : isMultiRow
            ? `Row ${activeRowIdx + 1} of ${rows.length} failed`
            : 'Claim failed',
        description: isEmailFailure
          ? 'Your claim was NOT recorded because the notification email could not be delivered. Click "Submit" again to retry.'
          : err instanceof Error
            ? err.message
            : String(err),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmitExisting = useMemo(() => {
    if (rows.length === 0 || submitting) return false;
    return mode === 'existing' && selectedExistingId !== null;
  }, [rows.length, submitting, mode, selectedExistingId]);

  const activeDraft = drafts.get(activeRowKey);

  const selectExistingInvoice = (invoiceId: number) => {
    setSelectedExistingId(invoiceId);
    if (!activeRowKey) return;
    setExistingTargets((prev) => {
      const next = new Map(prev);
      next.set(activeRowKey, invoiceId);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-5xl"
        // Toasts render in a portal OUTSIDE this dialog. Without these, the
        // pointer-down on the toast's close button fires Radix's default
        // "outside" handler and dismisses the wizard mid-flow. The user
        // already has Cancel + the X button to close intentionally.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {isMultiRow ? 'Claim selected commissions' : 'Claim unclaimed invoice'}
          </DialogTitle>
          <DialogDescription>
            Find and claim unassigned commissions.
          </DialogDescription>
        </DialogHeader>

        {isMultiRow && activeRow && (
          <ClaimWizardRowNav
            rows={rows}
            activeRowIdx={activeRowIdx}
            activeRow={activeRow}
            submittedCount={submittedRowKeys.size}
            isRowDone={isRowDone}
            submitting={submitting}
            onPrev={() => goRow(-1)}
            onNext={() => goRow(1)}
            onJump={jumpToRow}
          />
        )}

        {!isMultiRow && rows.length > 0 ? (
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-sm">
            <p className="mb-1 font-medium">Selected unclaimed invoice</p>
            <p className="leading-snug">
              <span className="font-mono font-medium">
                TRAMS {rows[0].tramsInvoiceNo}/{rows[0].tramsBookingNo}
              </span>
              <span className="text-muted-foreground">
                {' '}
                · {rows[0].primaryTraveler ?? '—'} ·{' '}
                {rows[0].supplierName ?? '—'}
              </span>
            </p>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Claim method">
          <Button
            type="button"
            variant={mode === 'existing' ? 'default' : 'outline'}
            aria-pressed={mode === 'existing'}
            onClick={() => handleModeChange('existing')}
            disabled={submitting}
            className="h-auto min-h-9 w-full whitespace-normal px-2 py-2 text-sm"
          >
            Attach to an existing invoice
          </Button>
          <Button
            type="button"
            variant={mode === 'new' ? 'default' : 'outline'}
            aria-pressed={mode === 'new'}
            onClick={() => handleModeChange('new')}
            disabled={submitting}
            className="h-auto min-h-9 w-full whitespace-normal px-2 py-2 text-sm"
          >
            Create a new invoice
          </Button>
        </div>

        {mode === 'existing' && (
          <div className="space-y-2">
            <label htmlFor="claim-wizard-search" className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Search by Invoice # or Confirmation #
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                id="claim-wizard-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type at least 2 characters…"
                className="pl-8"
              />
            </div>
            <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700" aria-live="polite" aria-busy={searchResults.isFetching || undefined}>
              {searchResults.isFetching ? (
                <div className="flex items-center justify-center p-6 text-sm text-gray-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Searching…
                </div>
              ) : (searchResults.data?.items.length ?? 0) === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">
                  {search.trim().length < 2
                    ? 'Start typing to search your invoices.'
                    : 'No invoices match that search.'}
                </div>
              ) : (
                <RadioGroup
                  value={
                    selectedExistingId != null
                      ? String(selectedExistingId)
                      : undefined
                  }
                  onValueChange={(v) => selectExistingInvoice(Number(v))}
                  aria-label="Matching invoices"
                  className="gap-0 divide-y divide-gray-100 dark:divide-gray-800"
                >
                  {searchResults.data!.items.map((item) => (
                    <SearchResultRow
                      key={item.id}
                      item={item}
                      selected={selectedExistingId === item.id}
                    />
                  ))}
                </RadioGroup>
              )}
            </div>
          </div>
        )}

        {mode === 'new' && activeRow && (
          <div className="max-h-[min(70vh,720px)] overflow-y-auto pr-1">
            <NewInvoiceStep
              // Remount per row so each gets a fresh RHF instance.
              key={activeRowKey}
              ref={stepRef}
              row={activeRow}
              initialFormValues={activeDraft?.formValues}
              initialTripSection={
                activeDraft?.tripSection ?? DEFAULT_TRIP_SECTION
              }
              invoiceIdempotencyKey={
                invoiceIdempotencyKeys[activeRowKey] ??
                deriveInvoiceIdempotencyKey(userId, activeRow)
              }
            />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          {activeRowPending && showSubmitContinue && (
            <Button
              onClick={
                mode === 'existing'
                  ? handleSubmitExisting
                  : handleSubmitNewRow
              }
              disabled={
                mode === 'existing' ? !canSubmitExisting : submitting
              }
            >
              {submitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Submit & continue
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          )}
          {activeRowPending && showSubmitFinish && (
            <Button
              onClick={
                mode === 'existing'
                  ? handleSubmitExisting
                  : handleSubmitNewRow
              }
              disabled={
                submitting ||
                (mode === 'existing' ? !canSubmitExisting : false)
              }
            >
              {submitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isMultiRow ? 'Submit & finish' : 'Submit claim'}
            </Button>
          )}
          {isMultiRow && !activeRowPending && pendingRows.length > 0 && (
            <p className="text-sm text-muted-foreground">
              This commission is already submitted — switch to a pending one
              ({pendingRows.length} left).
            </p>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Compact carousel + step dots (or jump menu for large batches). */
function ClaimWizardRowNav({
  rows,
  activeRowIdx,
  activeRow,
  submittedCount,
  isRowDone,
  submitting,
  onPrev,
  onNext,
  onJump,
}: {
  rows: UnclaimedInvoiceRow[];
  activeRowIdx: number;
  activeRow: UnclaimedInvoiceRow;
  submittedCount: number;
  isRowDone: (r: UnclaimedInvoiceRow) => boolean;
  submitting: boolean;
  onPrev: () => void;
  onNext: () => void;
  onJump: (idx: number) => void;
}) {
  const useJumpMenu = rows.length > 10;
  const done = isRowDone(activeRow);
  const summary = formatCommissionSummary(activeRow);

  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 sm:px-4">
      <div className="flex items-center gap-2 sm:gap-3">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0 sm:h-9 sm:w-9"
          onClick={onPrev}
          disabled={activeRowIdx === 0 || submitting}
          aria-label="Previous commission"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5 text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5">
            <p className="text-sm font-semibold tabular-nums leading-none">
              Commission {activeRowIdx + 1} of {rows.length}
            </p>
            {done && (
              <Badge
                variant="outline"
                className="h-5 border-[#10b981]/40 px-1.5 text-[10px] text-[#10b981]"
              >
                Submitted
              </Badge>
            )}
            {submittedCount > 0 && !done && (
              <span className="text-xs text-muted-foreground">
                ({submittedCount} done)
              </span>
            )}
          </div>

          <p
            className="w-full truncate text-sm text-muted-foreground"
            title={summary}
          >
            <span className="font-mono font-medium text-foreground">
              {activeRow.tramsInvoiceNo}/{activeRow.tramsBookingNo}
            </span>
            <span>
              {' '}
              · {activeRow.primaryTraveler ?? '—'}
              {activeRow.supplierName ? ` · ${activeRow.supplierName}` : ''}
            </span>
          </p>

          {useJumpMenu ? (
            <Select
              value={String(activeRowIdx)}
              onValueChange={(v) => onJump(parseInt(v, 10))}
              disabled={submitting}
            >
              <SelectTrigger className="mt-0.5 h-8 w-full max-w-md text-xs">
                <SelectValue placeholder="Jump to commission" />
              </SelectTrigger>
              <SelectContent>
                {rows.map((r, idx) => (
                  <SelectItem key={`${rowKey(r)}-${idx}`} value={String(idx)}>
                    {idx + 1}. {formatCommissionSummary(r)}
                    {isRowDone(r) ? ' (submitted)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div
              className="flex items-center justify-center gap-1.5 pt-0.5"
              role="tablist"
              aria-label="Commissions in this claim batch"
            >
              {rows.map((r, idx) => {
                const isActive = idx === activeRowIdx;
                const rowDone = isRowDone(r);
                return (
                  <button
                    key={`${rowKey(r)}-${idx}`}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-label={`Commission ${idx + 1}${rowDone ? ', submitted' : ''}`}
                    title={formatCommissionSummary(r)}
                    disabled={submitting}
                    onClick={() => onJump(idx)}
                    className={cn(
                      'h-2 w-2 rounded-full transition-colors',
                      isActive
                        ? 'bg-[#ff7540] ring-2 ring-[#ff7540]/30'
                        : rowDone
                          ? 'bg-[#10b981]'
                          : 'bg-muted-foreground/30 hover:bg-muted-foreground/50',
                    )}
                  />
                );
              })}
            </div>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0 sm:h-9 sm:w-9"
          onClick={onNext}
          disabled={activeRowIdx >= rows.length - 1 || submitting}
          aria-label="Next commission"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function SearchResultRow({
  item,
  selected,
}: {
  item: SearchableInvoiceRow;
  selected: boolean;
}) {
  const inputId = `claim-existing-invoice-${item.id}`;
  return (
    <div
      className={cn(
        'flex items-start gap-3 px-3 py-2 transition-colors hover:bg-muted/60',
        selected && 'bg-[#ff7540]/10',
      )}
    >
      <RadioGroupItem
        value={String(item.id)}
        id={inputId}
        className="mt-0.5 shrink-0"
      />
      <label
        htmlFor={inputId}
        className="min-w-0 flex-1 cursor-pointer text-left"
      >
        <span className="font-mono text-xs">
          {item.invoiceNumber ?? `Invoice #${item.id}`}
        </span>
        <div className="text-xs text-gray-500">
          {item.primaryTraveler ?? '—'} · {item.supplierName ?? '—'} ·{' '}
          {item.tripName?.trim() || `Trip ${item.tripId}`}
        </div>
        {item.confirmationNumber && (
          <div className="text-xs text-gray-400">
            Confirm #{item.confirmationNumber}
          </div>
        )}
      </label>
    </div>
  );
}
